import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { ActivityCard, BeeTypeCard, CommunityCard, ConsistencyCard, HabitCard, HiveEvolutionCard, IntroCard, MilestonesCard, PercentileCard, SocialCard } from '../../components/recap/RecapCards';
import { RecapShareCard } from '../../components/recap/RecapShareCard';
import { PageHeader } from '../../components/PageHeader';
import { SegmentedTabs } from '../../components/SegmentedTabs';
import { Button } from '../../components/ui/Button';
import { recaps } from '../../data/engagement';
import { useLocale } from '../../contexts/LocaleContext';
import type { RecapPeriod } from '../../types/engagement';
import { percentileEligible, recapCards, selectBeeArchetype, type RecapCardId } from '../../utils/recap';
import { useScreenInit } from '../../useScreenInit.js';

/**
 * MOB/Stats/Recap — monthly / annual recap as a sequence of story cards ending in a
 * shareable summary. One engine, two periods (Client Review — Revision 03).
 */
export function Recap() {
  const {
    t
  } = useLocale();
  const reduceMotion = useReducedMotion();
  const screenInit = useScreenInit() as {
    recapPeriod?: RecapPeriod;
    recapStep?: RecapCardId;
  };
  const [period, setPeriod] = useState<RecapPeriod>(screenInit.recapPeriod ?? 'month');
  const recap = recaps.find(item => item.period === period) ?? recaps[0];
  const cards = useMemo(() => recapCards(recap), [recap]);
  const archetype = useMemo(() => selectBeeArchetype(recap), [recap]);
  const initialIndex = Math.max(0, cards.indexOf(screenInit.recapStep ?? 'intro'));
  const [index, setIndex] = useState(initialIndex);
  const defaultCommunity = (r: typeof recap) => r.communityContext.find(item => percentileEligible(item))?.communityId ?? r.communityContext[0]?.communityId ?? '';
  const [communityId, setCommunityId] = useState(defaultCommunity(recap));
  const changePeriod = (next: RecapPeriod) => {
    const nextRecap = recaps.find(item => item.period === next) ?? recaps[0];
    setPeriod(next);
    setIndex(0);
    setCommunityId(defaultCommunity(nextRecap));
  };
  const current = cards[Math.min(index, cards.length - 1)];
  const last = cards.length - 1;
  const renderCard = (id: RecapCardId) => {
    switch (id) {
      case 'intro':
        return <IntroCard recap={recap} data-id="element-2951" data-mp="rzbwxwz5pp6a" />;
      case 'beeType':
        return <BeeTypeCard recap={recap} archetype={archetype} data-id="element-2952" data-mp="rxshu9p8lk63" />;
      case 'hive':
        return <HiveEvolutionCard recap={recap} data-id="element-2953" data-mp="wz7yzqoan6j7" />;
      case 'consistency':
        return <ConsistencyCard recap={recap} data-id="element-2954" data-mp="izfe4bzg7kj5" />;
      case 'habit':
        return <HabitCard recap={recap} data-id="element-2955" data-mp="nxqmsje0bozs" />;
      case 'activity':
        return <ActivityCard recap={recap} data-id="element-2956" data-mp="nya3jwrx87c8" />;
      case 'community':
        return <CommunityCard recap={recap} data-id="element-2957" data-mp="2dcezklqif8yc" />;
      case 'social':
        return <SocialCard recap={recap} data-id="element-2958" data-mp="2ibriwwi69xw6" />;
      case 'percentile':
        return <PercentileCard recap={recap} communityId={communityId} onChoose={setCommunityId} data-id="element-2959" data-mp="2iazz0ixxtolq" />;
      case 'milestones':
        return <MilestonesCard recap={recap} data-id="element-2960" data-mp="2sdc4qcmxdagt" />;
      case 'share':
        return <RecapShareCard recap={recap} archetype={archetype} communityId={communityId} data-id="element-2961" data-mp="2ni6ifcpm1ts7" />;
    }
  };
  return <div className="px-2 pb-2" data-id="element-2962" data-mp="319bantqnfjsi">
      <PageHeader title={t('eng.recap.title')} subtitle={t('eng.recap.subtitle')} showBack data-id="element-2963" data-mp="2whbbw4mbewxi" />

      <SegmentedTabs label={t('eng.recap.title')} value={period} onChange={changePeriod} tabs={[{
      id: 'month',
      label: t('eng.recap.month')
    }, {
      id: 'year',
      label: t('eng.recap.year')
    }]} data-id="element-2964" data-mp="3b67kq3w74dz6" />

      <ol className="mt-3 flex gap-[3px]" aria-label={t('eng.recap.storyAria', {
      n: index + 1,
      total: cards.length
    })} data-id="element-2965" data-mp="3k6cgfgs11nxi">
        {cards.map((id, i) => <li key={id} className="flex-1" data-id="element-2966" data-mp="1psldf047e2b1" data-mpi={i}>
            <button type="button" onClick={() => setIndex(i)} aria-label={t('eng.recap.storyAria', {
          n: i + 1,
          total: cards.length
        })} aria-current={i === index ? 'step' : undefined} className="flex h-6 w-full items-center" data-id="element-2967" data-mp="1ptd53zuojfw1">
              <span className={`block h-[3px] w-full rounded-pill transition-colors duration-150 ease-brand ${i <= index ? 'bg-gold' : 'bg-sand-line'}`} data-id="element-2968" data-mp="1s5ottto4fxbg" />
            </button>
          </li>)}
      </ol>

      <div className="relative mt-1" aria-live="polite" data-id="element-2969" data-mp="14j8jqlgdojz6">
        <AnimatePresence mode="wait" initial={false} data-id="element-2970" data-mp="18j99v3cuajm2">
          <motion.div key={`${period}-${current}`} initial={reduceMotion ? {
          opacity: 0
        } : {
          opacity: 0,
          x: 12
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={reduceMotion ? {
          opacity: 0
        } : {
          opacity: 0,
          x: -12
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-2971" data-mp="18k25p9a9fopf">
            {renderCard(current)}
          </motion.div>
        </AnimatePresence>
      </div>

      {current !== 'share' && <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-2972" data-mp="i9s0v1zueclk">
          <Button variant="outline" disabled={index === 0} onClick={() => setIndex(i => Math.max(0, i - 1))} data-id="element-2973" data-mp="i7fjapxzy491">
            <ChevronLeftIcon size={16} aria-hidden="true" data-id="element-2974" data-mp="8bca504q95cl" />
            {t('eng.recap.prev')}
          </Button>
          <Button variant="primary" onClick={() => setIndex(i => Math.min(last, i + 1))} data-id="element-2975" data-mp="4a0qrccg5gcu">
            {t('eng.recap.next')}
            <ChevronRightIcon size={16} aria-hidden="true" data-id="element-2976" data-mp="3vh3iq603zqo4" />
          </Button>
        </div>}
      {current !== 'share' && <Button variant="quiet" fullWidth className="mt-1" onClick={() => setIndex(last)} data-id="element-2977" data-mp="3qo0nzujlib6c">
          {t('eng.recap.toShare')}
        </Button>}
      {current === 'share' && <Button variant="outline" fullWidth className="mt-2" onClick={() => setIndex(i => Math.max(0, i - 1))} data-id="element-2978" data-mp="3gsqkf2djtvqg">
          <ChevronLeftIcon size={16} aria-hidden="true" data-id="element-2979" data-mp="3coraj2mrxv62" />
          {t('eng.recap.prev')}
        </Button>}
    </div>;
}